// Web Audio API wrapper with named channels + per-instance HRTF spatialization.
//
// Pipeline per playing instance:
//   buffer -> ownGain -> [panner if spatial] -> channelGain -> master -> destination
//
// - Channels (ambient/music/narration/sfx/room) each have a gain node, so
//   per-channel volume is independent of master and of individual volumes.
//   `room` is the persistent-ambience bus (true room tone) that the era
//   crossfade never resets.
// - HANDLE-BASED: every play() returns a unique handle and is tracked
//   independently, so the SAME asset can sound from N positions at once (two
//   fans running one loop, several tapes playing together). Control an
//   instance precisely by its handle; the (id, channel) convenience methods
//   below act on a matching instance for the dev mixer + back-compat.
// - Spatialization is a per-instance PRESET (opts.spatial): 'point' (tight,
//   ~1.5 m — a fan), 'area' (room-filling but localized, ~6 m — a generator,
//   a boombox), or 'global' (no panner — everywhere; room tone). opts.at gives
//   the origin; when at.object is set the panner follows it via syncSpatial.
// - opts.detune offsets pitch in cents for subtle per-instance variation.
// - opts.exclusive fades out other tracks on the same channel first (legacy
//   single-slot; most callers now manage instances by handle instead).
// - Boots muted (master gain = 0). setMuted(false) restores the preferred
//   master level, which persists across reloads in sessionStorage (along with
//   each channel's volume). The muted flag itself does NOT persist — every
//   page load starts muted.

import { Vector3, type Camera, type Object3D } from 'three';
import { devBus } from '../debug/devBus';
import { SynthVoice } from './synthVoice';
import type { AudioAsset } from './manifest';

export type AudioChannel = 'ambient' | 'music' | 'narration' | 'sfx' | 'room';

/** How a sound sits in space:
 *  - point:  localized, lost within a couple metres (a fan).
 *  - area:   fills a zone but still localizes to its origin (a generator, a boombox).
 *  - global: everywhere — no attenuation or direction (room tone, a non-diegetic score). */
export type SpatialType = 'point' | 'area' | 'global';

// refDistance = inner "full-presence" core (gain ~unity within); rolloff = how
// fast the 'inverse' model attenuates beyond that. maxDistance = the audible
// reach (the outer sphere the viz draws): the 'inverse' panner IGNORES it
// (Web Audio spec), so AudioManager enforces it itself as a post-panner reach
// cutoff (A1 — see REACH_FLOOR) rather than letting the knob lie. A placement
// can override refDistance + maxDistance per source; rolloff stays preset-derived.
const SPATIAL_PRESETS: Record<'point' | 'area', { refDistance: number; rolloff: number; maxDistance: number }> = {
  point: { refDistance: 1.5, rolloff: 1.0, maxDistance: 7 },
  area: { refDistance: 6, rolloff: 0.6, maxDistance: 15 },
};

/** Effective spatial falloff radii for a placement — its per-source overrides
 *  if set, else the spatial-type preset. Returns null for non-spatial ('global')
 *  sounds. Pure (no panner needed), so the audio-source viz can size the source
 *  + falloff spheres even for silent/off-era placements. */
export function spatialFalloff(
  spatial: SpatialType | undefined,
  refOverride?: number,
  maxOverride?: number,
): { refDistance: number; maxDistance: number } | null {
  if (spatial === 'global') return null;
  const preset = SPATIAL_PRESETS[spatial === 'area' ? 'area' : 'point'];
  return {
    refDistance: refOverride && refOverride > 0 ? refOverride : preset.refDistance,
    maxDistance: maxOverride && maxOverride > 0 ? maxOverride : preset.maxDistance,
  };
}

export interface PlayAt {
  /** Track an Object3D's world position. */
  object?: Object3D;
  /** Fixed world position [x, y, z]. */
  position?: [number, number, number];
}

export interface PlayOptions {
  loop?: boolean;
  volume?: number;
  fadeIn?: number;
  channel?: AudioChannel;
  /** Fade out other tracks on the same channel before starting. */
  exclusive?: boolean;
  /** Spatialize via PannerNode. */
  at?: PlayAt;
  /** Spatial falloff preset. Defaults to 'point' when an origin is given; a
   *  'global' sound skips the panner entirely and plays everywhere. */
  spatial?: SpatialType;
  /** Per-source falloff overrides in metres (override the spatial-type preset). */
  refDistance?: number;
  maxDistance?: number;
  /** Pitch offset in cents (e.g. ±50) for per-instance variation. */
  detune?: number;
  /** Start playback this many seconds INTO the source (N1 clips — entering a
   *  region mid-way starts the sample mid-way). Ignored for synth assets
   *  (procedural voices have no inside to seek). */
  offset?: number;
  /** W1 — hard-cap a buffer source to this many seconds of playback (the source
   *  `start()` 3rd arg — Web Audio stops it exactly, sample-accurate, no
   *  per-frame check). This is how a clip's `out` actually caps the audio.
   *  Ignored for synth (generated to length) + looping sources (a bed keeps
   *  looping). */
  duration?: number;
  /** W1 — fade the tail out over this many seconds, scheduled to land at the end
   *  of a capped `duration`. No-op without a duration (an open-ended source has
   *  no known end to fade toward). */
  fadeOut?: number;
}

interface PlayingEntry {
  handle: string;
  id: string;
  channel: AudioChannel;
  /** A decoded sample's source node, or a procedural SynthVoice (A13) — both
   *  expose the same narrow surface the manager touches (start/stop/loop/
   *  onended), so everything downstream of `gain` is kind-agnostic. */
  source: AudioBufferSourceNode | SynthVoice;
  gain: GainNode;
  panner?: PannerNode;
  /** A1 — post-panner gain that enforces maxDistance as a real reach cutoff
   *  (the 'inverse' panner ignores maxDistance). Only present on spatial
   *  instances; the render tick rolls it 1 → REACH_FLOOR around maxDistance. */
  reachGain?: GainNode;
  /** A1 — last reach gain we wrote, so the per-frame cutoff is epsilon-gated. */
  lastReach?: number;
  trackedObject?: Object3D;
  /** A8 — last panner position we wrote (the ramp TARGET, not the live param),
   *  so the per-frame epsilon-gate has a stable thing to compare against. Lives
   *  on the entry, so it's evicted when the instance is. */
  lastPannerPos?: Vector3;
}

const SS_PREFIX = 'lec_audio_';
const _pos = new Vector3();
const _fwd = new Vector3();
const _up = new Vector3();
const _camPos = new Vector3(); // A1 — listener pos saved before the panner loop reuses _pos

// Fast fade-out on exclusive-replace so the swap doesn't sound like two
// tracks audible simultaneously.
const EXCLUSIVE_FADE_OUT = 0.05;

// A7 — floor for RESTORED gains (master + channels). A stored value below this
// is treated as "left muted last session" and discarded for the default, so a
// zeroed bus can't silently survive a reload as permanent silence. A live
// set-to-zero still works within a session; only the persisted restore floors.
const MIN_RESTORED_GAIN = 0.05;

// A6 — short ramp for gain changes (per-instance volume AND master mute/level)
// so a toggle or slider doesn't click while beds run.
const GAIN_RAMP = 0.08;

// A8 — spatial write discipline. syncSpatial ran every frame writing 9 listener
// params + 3 per tracked panner unconditionally: stepped .value = zipper on fast
// moves + constant cross-thread churn for a static scene. So: epsilon-GATE each
// group first (compare the camera/object value to the last TARGET we wrote — not
// the live param, which is mid-ramp), then ramp ONLY on change over ~a frame.
const SPATIAL_POS_EPS = 0.01;  // metres — below this the listener/panner didn't really move
const SPATIAL_DIR_EPS = 0.002; // unit-vector delta (~0.1°) — below this the camera didn't really turn
const SPATIAL_RAMP = 0.03;     // ramp window (~2 frames) that smooths the per-frame steps

// A1 — make maxDistance an HONEST audible reach. John ruled the panner model
// stays 'inverse' (its near-field feel), but under 'inverse' the Web Audio spec
// ignores maxDistance entirely — so the per-source `maxDistance` knob, the
// gizmo's outer ring, and the timeline tooltip all lied (sources never fell
// silent with distance). Fix: a per-instance `reachGain` AFTER the panner that
// the render tick rolls from 1 (within reach) down to REACH_FLOOR just beyond
// maxDistance. This keeps inverse's character, leaves the engine's ownGain (bed
// volume) untouched, and makes BOTH "die to silence" (floor 0) and "a room where
// everything murmurs" (floor > 0) reachable by editing one constant.
const REACH_FLOOR = 0;     // gain a source settles to beyond its reach (0 = silent; >0 = faint bleed)
const REACH_BAND = 0.25;   // fade-out band width as a fraction of maxDistance (click-free rolloff past reach)
const REACH_EPS = 0.01;    // skip a reach write below this gain delta (static-scene = no writes)

// E2 — entry low-pass on the ambient bus: the interior tone OPENS as you cross
// the doorway (openness 1 = clear inside) and CLOSES when you're outside
// (openness 0 = heard muffled through the wall). Starting values — the cutoff
// range + the muffle feel are John's ear. Default is OPEN (transparent), so the
// filter is inert until setEntryOpenness() is driven by the view transition.
const ENTRY_LP_OPEN = 20000;   // Hz — effectively transparent (no audible filtering)
const ENTRY_LP_MUFFLED = 1200; // Hz — muffled-through-the-wall when fully outside
const ENTRY_SWEEP = 0.6;       // s — the doorway-tween sweep time

function loadStored(key: string): number | null {
  try {
    const v = sessionStorage.getItem(SS_PREFIX + key);
    if (v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function saveStored(key: string, value: number): void {
  try { sessionStorage.setItem(SS_PREFIX + key, String(value)); } catch { /* ignored */ }
}

export class AudioManager {
  private ctx: AudioContext;
  private master: GainNode;
  private channels: Record<AudioChannel, GainNode>;
  /** E2 — entry low-pass spliced into the ambient bus (ambient → entryLP → master). */
  private entryLowpass!: BiquadFilterNode;
  private buffers: Map<string, AudioBuffer> = new Map();
  /** Registry view (A13): play() consults this to know an id's kind — a synth
   *  entry builds a SynthVoice from its patch instead of needing a buffer. */
  private assets: Map<string, AudioAsset> = new Map();
  /** Keyed by unique handle — many instances of one asset can coexist. */
  private playing: Map<string, PlayingEntry> = new Map();
  private handleSeq = 0;
  /** A3 — ids whose load() threw: isLoaded already reports false (no buffer),
   *  but this stops load() re-fetching a known-dead url and lets the not-loaded
   *  warn distinguish "failed" from "still streaming". */
  private failed: Set<string> = new Set();
  /** A3 — ids we've already warned about in play(); without this a clip/cue on
   *  a missing asset re-warns every frame (the reconciler calls play() at 60fps
   *  while the handle never lands). */
  private warnedMissing: Set<string> = new Set();

  private _muted = true;
  private _preferredMaster: number;

  // A8 — last listener target we wrote per group (pos / forward / up). Seeded to
  // Infinity so the first sync always counts as "changed" and writes once.
  private _lisPos = new Vector3(Infinity, Infinity, Infinity);
  private _lisFwd = new Vector3(Infinity, Infinity, Infinity);
  private _lisUp = new Vector3(Infinity, Infinity, Infinity);
  // A8 — instrumentation: count of actual spatial write-groups committed. Static
  // scene → stays flat frame-to-frame; motion → climbs. (Read via the getter.)
  private _spatialWrites = 0;

  constructor() {
    this.ctx = new AudioContext();
    this.master = this.ctx.createGain();
    this.master.connect(this.ctx.destination);

    // Discard a near-zero stored value — if a previous session left this at 0,
    // we'd unmute into silence and feel broken.
    const storedMaster = loadStored('master');
    this._preferredMaster = (storedMaster != null && storedMaster >= MIN_RESTORED_GAIN) ? storedMaster : 0.8;
    this.master.gain.value = 0; // start muted regardless of stored level

    this.channels = {
      ambient: this.makeChannel('ambient', 0.5),
      music: this.makeChannel('music', 0.8),
      narration: this.makeChannel('narration', 1),
      sfx: this.makeChannel('sfx', 0.8),
      room: this.makeChannel('room', 0.5),
    };

    // E2 — splice the entry low-pass into the ambient bus (ambient → entryLP →
    // master). Starts fully OPEN (transparent), so the scene sounds identical
    // until the view transition drives setEntryOpenness().
    this.entryLowpass = this.ctx.createBiquadFilter();
    this.entryLowpass.type = 'lowpass';
    this.entryLowpass.frequency.value = ENTRY_LP_OPEN;
    this.entryLowpass.Q.value = 0.5; // gentle, no resonant bump
    this.channels.ambient.disconnect();
    this.channels.ambient.connect(this.entryLowpass);
    this.entryLowpass.connect(this.master);
  }

  // N7 — transient per-channel duck (1 = no duck). The node gain is always
  // channelBase × channelDuck, so a duck dips the bus WITHOUT clobbering the
  // user's persisted channel volume (the base), and restores cleanly.
  private channelBase = {} as Record<AudioChannel, number>;
  private channelDuck = {} as Record<AudioChannel, number>;

  private makeChannel(name: AudioChannel, defaultGain: number): GainNode {
    const g = this.ctx.createGain();
    // A7 — mirror the master guard: a persisted near-zero would otherwise
    // restore a dead channel that silently swallows everything routed to it.
    const stored = loadStored('channel_' + name);
    const base = (stored != null && stored >= MIN_RESTORED_GAIN) ? stored : defaultGain;
    this.channelBase[name] = base;
    this.channelDuck[name] = 1;
    g.gain.value = base;
    g.connect(this.master);
    return g;
  }

  /** N7 — duck a channel to `factor` × its base (1 = restore), ramped so a
   *  scripted clip can play UNDER a visitor's tape (narration ducks music) and
   *  the bus lifts back when the clip ends. Transient: doesn't touch the stored
   *  channel volume. */
  duckChannel(channel: AudioChannel, factor: number): void {
    this.channelDuck[channel] = Math.max(0, factor);
    this.rampParam(this.channels[channel].gain, this.channelBase[channel] * this.channelDuck[channel], GAIN_RAMP);
  }

  /** Story-flow #7 — the room comes alive GRADUALLY after the generator: swell
   *  the ambient bed bus up from silence to its base level over `seconds`. The
   *  generator's own pull-start one-shot rides the sfx channel, so it still
   *  cracks in at full — only the steady room beds (fan, sewing, the gen hum)
   *  fade up. Transient like a duck: it schedules the gain node directly and
   *  lands exactly on base×duck, so the stored channel volume + steady state are
   *  untouched. `seconds <= 0` is an ordinary (instant) ramp — no fade. */
  fadeInRoom(seconds: number): void {
    const g = this.channels.ambient.gain;
    const target = this.channelBase.ambient * this.channelDuck.ambient;
    if (seconds <= 0) { this.rampParam(g, target, GAIN_RAMP); return; }
    const now = this.ctx.currentTime;
    g.cancelScheduledValues(now);
    g.setValueAtTime(0, now);
    g.linearRampToValueAtTime(target, now + seconds);
  }

  get contextState(): AudioContextState {
    return this.ctx.state;
  }

  /** Monotonic audio-clock seconds — advances in real time regardless of the
   *  time-of-day clock (paused/scrubbed), so it drives bed LFOs + reshuffle. */
  get audioTime(): number {
    return this.ctx.currentTime;
  }

  get muted(): boolean {
    return this._muted;
  }

  setMuted(muted: boolean): void {
    this._muted = muted;
    // A6 — ramp instead of stepping: a hard 0↔level jump clicks while beds run.
    this.rampParam(this.master.gain, muted ? 0 : this._preferredMaster);
  }

  async resume(): Promise<void> {
    if (this.ctx.state === 'suspended') await this.ctx.resume();
  }

  /** Register the manifest so play() knows each id's kind (A13). Samples still
   *  need load(); synth entries are playable immediately (zero bytes). */
  registerAssets(assets: AudioAsset[]): void {
    for (const a of assets) this.assets.set(a.id, a);
  }

  async load(id: string, url: string): Promise<void> {
    if (this.buffers.has(id) || this.failed.has(id)) return; // A3: don't re-fetch a known-dead url
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const ab = await res.arrayBuffer();
      const buf = await this.ctx.decodeAudioData(ab);
      this.buffers.set(id, buf);
      // T3 — announce the now-known real length so a clip laid before its source
      // decoded can correct its placeholder duration (asset-length truth).
      devBus.emit('audio:loaded', { id, duration: buf.duration });
    } catch (err) {
      this.failed.add(id); // A3: record the failure so isLoaded stays false and we stop retrying
      console.warn('[audio] failed to load', id, url, err);
    }
  }

  /** True if an asset is ready to play — a decoded sample buffer, or a
   *  registered synth patch (always ready; built live). */
  isLoaded(id: string): boolean {
    return this.buffers.has(id) || this.assets.get(id)?.kind === 'synth';
  }

  /** Decoded length of a SAMPLE in seconds; undefined for synth (generated to
   *  length — no fixed inside) or not-yet-loaded ids. N1 clips use this to
   *  stop seeking past the end of a short source. */
  assetDuration(id: string): number | undefined {
    return this.buffers.get(id)?.duration;
  }

  /** Start an instance and return its handle (for precise per-instance
   *  control). Returns '' if the asset isn't loaded. */
  play(id: string, opts: PlayOptions = {}): string {
    const asset = this.assets.get(id);
    const isSynth = asset?.kind === 'synth' && !!asset.patch;
    const buf = isSynth ? undefined : this.buffers.get(id);
    if (!isSynth && !buf) {
      // A3 — warn ONCE per id. A clip/cue scheduled on a missing or failed
      // asset reaches play() every frame it's live; without this gate that's a
      // 60fps warn storm. "failed" vs "not loaded" tells which it is.
      if (!this.warnedMissing.has(id)) {
        this.warnedMissing.add(id);
        console.warn(this.failed.has(id) ? '[audio] failed asset:' : '[audio] not loaded:', id);
      }
      return '';
    }
    const channel: AudioChannel = opts.channel ?? 'sfx';

    if (opts.exclusive) {
      // A5 — sweep ALL same-channel instances, including same-id. The new
      // handle is minted AFTER this loop, so the sweep can never hit it; the
      // old `entry.id !== id` exemption therefore only spared the PREVIOUS
      // same-id instance, so re-triggering an exclusive sound stacked phasing
      // copies instead of restarting cleanly.
      for (const [h, entry] of [...this.playing]) {
        if (entry.channel === channel) {
          this.stopEntry(h, EXCLUSIVE_FADE_OUT);
        }
      }
    }

    const handle = 'h' + (++this.handleSeq);
    // Build the source: a BufferSource for samples, a SynthVoice for patches —
    // both feed the same per-instance gain, so everything downstream (panner,
    // channel, mixer, fades) is kind-agnostic (A13).
    const gain = this.ctx.createGain();
    let source: AudioBufferSourceNode | SynthVoice;
    if (isSynth) {
      const voice = new SynthVoice(this.ctx, asset!.patch!, { loop: !!opts.loop, detune: opts.detune });
      voice.output.connect(gain);
      source = voice;
    } else {
      const s = this.ctx.createBufferSource();
      s.buffer = buf!;
      s.loop = !!opts.loop;
      if (opts.detune) {
        try { s.detune.value = opts.detune; } catch { /* unsupported */ }
      }
      s.connect(gain);
      source = s;
    }

    let panner: PannerNode | undefined;
    let reachGain: GainNode | undefined;
    let trackedObject: Object3D | undefined;
    const wantsPanner = !!(opts.at && (opts.at.object || opts.at.position)) && opts.spatial !== 'global';
    if (wantsPanner && opts.at) {
      panner = this.createPanner(opts.at, opts.spatial ?? 'point', opts.refDistance, opts.maxDistance);
      // A1: gain → panner → reachGain → channel. The reachGain enforces the
      // maxDistance reach the 'inverse' panner ignores (rolled by syncSpatial).
      reachGain = this.ctx.createGain();
      gain.connect(panner);
      panner.connect(reachGain);
      reachGain.connect(this.channels[channel]);
      trackedObject = opts.at.object;
    } else {
      gain.connect(this.channels[channel]);
    }

    // W1 — the full clip envelope, scheduled on the audio clock AT START (no
    // per-frame reliance, no setTimeout drift): a buffer source is hard-capped
    // to `duration` of its buffer (start's 3rd arg — Web Audio stops it exactly,
    // no click), fades in over `fadeIn`, and fades out over `fadeOut` confined to
    // the tail of that duration. Synth voices have no inside to seek/cap
    // (generated to length) → they ignore offset/duration. A looping source (a
    // bed) ignores the cap so it keeps looping. Both fades are clamped to fit a
    // short clip so they can't overlap into a click.
    const target = opts.volume ?? 1;
    const t0 = this.ctx.currentTime;
    const capped = source instanceof AudioBufferSourceNode && !opts.loop && opts.duration !== undefined && opts.duration > 0;
    const capDur = capped ? (opts.duration as number) : undefined;
    let fadeIn = opts.fadeIn && opts.fadeIn > 0 ? opts.fadeIn : 0;
    let fadeOut = opts.fadeOut && opts.fadeOut > 0 ? opts.fadeOut : 0;
    if (capDur !== undefined) {
      const maxFade = capDur * 0.45; // leave a sustain in the middle — both ends must fit
      if (fadeIn > maxFade) fadeIn = maxFade;
      if (fadeOut > maxFade) fadeOut = maxFade;
    }
    if (fadeIn > 0) {
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(target, t0 + fadeIn);
    } else {
      gain.gain.setValueAtTime(target, t0);
    }
    if (fadeOut > 0 && capDur !== undefined) {
      const end = t0 + capDur;
      gain.gain.setValueAtTime(target, Math.max(t0 + fadeIn, end - fadeOut)); // anchor → confine the fade to the tail
      gain.gain.linearRampToValueAtTime(0, end);
    }
    const off = opts.offset && opts.offset > 0 ? opts.offset : 0;
    if (source instanceof AudioBufferSourceNode) {
      if (capDur !== undefined) source.start(0, off, capDur);
      else if (off > 0) source.start(0, off);
      else source.start();
    } else {
      source.start(); // synth — no inside to seek or cap
    }
    source.onended = () => {
      // Natural end (a non-looping source finished) — stopEntry didn't run, so
      // the manager still owns the handle. Free it AND its graph nodes (A9) and
      // notify, the same shape as an explicit stop.
      const e = this.playing.get(handle);
      if (e?.source === source) {
        this.playing.delete(handle);
        this.disconnectEntry(e);
        devBus.emit('audio:stop', { id, channel, handle });
      }
    };
    this.playing.set(handle, { handle, id, channel, source, gain, panner, reachGain, trackedObject });
    devBus.emit('audio:play', { id, channel, handle });
    return handle;
  }

  private createPanner(at: PlayAt, spatial: SpatialType, refOverride?: number, maxOverride?: number): PannerNode {
    const p = this.ctx.createPanner();
    p.panningModel = 'HRTF';
    p.distanceModel = 'inverse';
    const preset = SPATIAL_PRESETS[spatial === 'area' ? 'area' : 'point'];
    const fall = spatialFalloff(spatial, refOverride, maxOverride) ?? { refDistance: preset.refDistance, maxDistance: preset.maxDistance };
    p.refDistance = fall.refDistance;
    p.rolloffFactor = preset.rolloff;
    p.maxDistance = fall.maxDistance;

    let x = 0, y = 0, z = 0;
    if (at.object) {
      at.object.getWorldPosition(_pos);
      x = _pos.x; y = _pos.y; z = _pos.z;
    } else if (at.position) {
      [x, y, z] = at.position;
    }
    this.setPannerPos(p, x, y, z);
    return p;
  }

  private setPannerPos(p: PannerNode, x: number, y: number, z: number): void {
    if (p.positionX) {
      p.positionX.value = x;
      p.positionY.value = y;
      p.positionZ.value = z;
    } else {
      (p as PannerNode & { setPosition: (x: number, y: number, z: number) => void })
        .setPosition(x, y, z);
    }
  }

  /** Number of spatial write-groups committed since boot (A8 instrumentation).
   *  Sample it across frames: flat while the scene is static, climbing on motion. */
  get spatialWrites(): number {
    return this._spatialWrites;
  }

  /** True if `cur` has drifted past `eps` from the last-written `cache` (seeded
   *  to Infinity, so the first call always reports changed). */
  private changed(cache: Vector3, cur: Vector3, eps: number): boolean {
    return cache.distanceToSquared(cur) > eps * eps;
  }

  /** Sync listener orientation to camera + refresh tracked-object panner positions.
   *  Call from the render tick. A8: each group is epsilon-gated against the last
   *  TARGET and only written (ramped on the modern API, set on the legacy one)
   *  when it actually moved — a static scene writes nothing. */
  syncSpatial(camera: Camera): void {
    const l = this.ctx.listener;
    camera.getWorldPosition(_pos);
    _camPos.copy(_pos); // A1 — keep the listener pos; the panner loop reuses _pos for sources
    _fwd.set(0, 0, -1).applyQuaternion(camera.quaternion);
    _up.set(0, 1, 0).applyQuaternion(camera.quaternion);

    if (l.positionX) {
      // Modern AudioParam API — ramp the changed group(s) over ~a frame.
      if (this.changed(this._lisPos, _pos, SPATIAL_POS_EPS)) {
        this.rampParam(l.positionX, _pos.x, SPATIAL_RAMP);
        this.rampParam(l.positionY, _pos.y, SPATIAL_RAMP);
        this.rampParam(l.positionZ, _pos.z, SPATIAL_RAMP);
        this._lisPos.copy(_pos); this._spatialWrites++;
      }
      if (this.changed(this._lisFwd, _fwd, SPATIAL_DIR_EPS)) {
        this.rampParam(l.forwardX, _fwd.x, SPATIAL_RAMP);
        this.rampParam(l.forwardY, _fwd.y, SPATIAL_RAMP);
        this.rampParam(l.forwardZ, _fwd.z, SPATIAL_RAMP);
        this._lisFwd.copy(_fwd); this._spatialWrites++;
      }
      if (this.changed(this._lisUp, _up, SPATIAL_DIR_EPS)) {
        this.rampParam(l.upX, _up.x, SPATIAL_RAMP);
        this.rampParam(l.upY, _up.y, SPATIAL_RAMP);
        this.rampParam(l.upZ, _up.z, SPATIAL_RAMP);
        this._lisUp.copy(_up); this._spatialWrites++;
      }
    } else {
      // Legacy setPosition/setOrientation can't ramp — set on change only.
      const ll = l as AudioListener & {
        setPosition: (x: number, y: number, z: number) => void;
        setOrientation: (
          fx: number, fy: number, fz: number,
          ux: number, uy: number, uz: number,
        ) => void;
      };
      if (this.changed(this._lisPos, _pos, SPATIAL_POS_EPS)) {
        ll.setPosition(_pos.x, _pos.y, _pos.z);
        this._lisPos.copy(_pos); this._spatialWrites++;
      }
      if (this.changed(this._lisFwd, _fwd, SPATIAL_DIR_EPS) || this.changed(this._lisUp, _up, SPATIAL_DIR_EPS)) {
        ll.setOrientation(_fwd.x, _fwd.y, _fwd.z, _up.x, _up.y, _up.z);
        this._lisFwd.copy(_fwd); this._lisUp.copy(_up); this._spatialWrites++;
      }
    }

    for (const entry of this.playing.values()) {
      if (entry.panner && entry.trackedObject) {
        entry.trackedObject.getWorldPosition(_pos);
        if (!entry.lastPannerPos) entry.lastPannerPos = new Vector3(Infinity, Infinity, Infinity);
        if (this.changed(entry.lastPannerPos, _pos, SPATIAL_POS_EPS)) {
          this.rampPannerPos(entry.panner, _pos.x, _pos.y, _pos.z);
          entry.lastPannerPos.copy(_pos); this._spatialWrites++;
        }
        // A1 — reach cutoff. Depends on BOTH listener and source position, so
        // recompute each frame (cheap) but gate the WRITE on the reach value
        // moving (static scene → no writes, like the position gate above).
        if (entry.reachGain) {
          const reach = this.reachAt(_camPos.distanceTo(_pos), entry.panner.maxDistance);
          if (entry.lastReach === undefined || Math.abs(reach - entry.lastReach) > REACH_EPS) {
            this.rampParam(entry.reachGain.gain, reach, SPATIAL_RAMP);
            entry.lastReach = reach; this._spatialWrites++;
          }
        }
      }
    }
  }

  /** A1 — reach multiplier at distance d for a source whose authored reach is R:
   *  full (1) within R, a linear roll to REACH_FLOOR over a band just beyond,
   *  then clamped at the floor. R ≤ 0 (no maxDistance) → always full. */
  private reachAt(d: number, R: number): number {
    if (!(R > 0) || d <= R) return 1;
    return Math.max(REACH_FLOOR, 1 - (d - R) / (R * REACH_BAND));
  }

  /** A8 — ramp a tracked panner to a new position (modern API) or set it
   *  (legacy). The caller already epsilon-gated, so this only runs on change. */
  private rampPannerPos(p: PannerNode, x: number, y: number, z: number): void {
    if (p.positionX) {
      this.rampParam(p.positionX, x, SPATIAL_RAMP);
      this.rampParam(p.positionY, y, SPATIAL_RAMP);
      this.rampParam(p.positionZ, z, SPATIAL_RAMP);
    } else {
      (p as PannerNode & { setPosition: (x: number, y: number, z: number) => void })
        .setPosition(x, y, z);
    }
  }

  private stopEntry(handle: string, fadeOut = 0): void {
    const entry = this.playing.get(handle);
    if (!entry) return;
    // Hand the HUD its stop now (at fade START, as before) and release our
    // ownership so a re-stop on the same handle is a no-op; the actual source
    // stop + node teardown finish on the audio clock (A9).
    this.playing.delete(handle);
    devBus.emit('audio:stop', { id: entry.id, channel: entry.channel, handle });
    this.endEntry(entry, fadeOut);
  }

  /** A9 — stop a (now-unowned) entry's source and free its graph nodes. The
   *  audible fade is an audio-clock gain ramp for either source kind. For a
   *  BufferSource the stop is scheduled ON the audio clock and onended frees the
   *  nodes — background-tab safe, no wall-clock setTimeout. A SynthVoice has no
   *  audio-clock onended for an explicit stop and already self-tears-down its
   *  internal graph on a short timer, so its post-fade voice-stop + node-free
   *  ride one cleanup timer (cleanup, NOT scheduling — the A12/U7 frame-clock
   *  FIRING rule is untouched; this only governs when freed nodes get GC'd). */
  private endEntry(entry: PlayingEntry, fadeOut: number): void {
    const src = entry.source;
    if (fadeOut > 0) {
      const now = this.ctx.currentTime;
      entry.gain.gain.cancelScheduledValues(now);
      entry.gain.gain.setValueAtTime(entry.gain.gain.value, now);
      entry.gain.gain.linearRampToValueAtTime(0, now + fadeOut);
      if (src instanceof AudioBufferSourceNode) {
        src.onended = () => this.disconnectEntry(entry);
        try { src.stop(now + fadeOut); } catch { this.disconnectEntry(entry); }
      } else {
        setTimeout(() => { try { src.stop(); } catch { /* already */ } this.disconnectEntry(entry); }, fadeOut * 1000);
      }
    } else if (src instanceof AudioBufferSourceNode) {
      src.onended = () => this.disconnectEntry(entry);
      try { src.stop(); } catch { this.disconnectEntry(entry); }
    } else {
      try { src.stop(); } catch { /* already */ }
      this.disconnectEntry(entry);
    }
  }

  /** Detach a finished instance's nodes so the gain/HRTF-panner subgraph can be
   *  GC'd instead of accumulating across bed restarts + era toggles (A9). A
   *  BufferSource exposes disconnect(); a SynthVoice self-disconnects its
   *  internal graph on stop(), so we free only what the manager built here. */
  private disconnectEntry(entry: PlayingEntry): void {
    if (entry.source instanceof AudioBufferSourceNode) {
      try { entry.source.disconnect(); } catch { /* already */ }
    }
    try { entry.gain.disconnect(); } catch { /* already */ }
    if (entry.panner) { try { entry.panner.disconnect(); } catch { /* already */ } }
    if (entry.reachGain) { try { entry.reachGain.disconnect(); } catch { /* already */ } } // A1
  }

  private findEntry(id: string, channel: AudioChannel): PlayingEntry | undefined {
    for (const e of this.playing.values()) {
      if (e.id === id && e.channel === channel) return e;
    }
    return undefined;
  }

  private entryWorldPosition(entry: PlayingEntry, out: Vector3): boolean {
    if (!entry.panner) return false;
    if (entry.trackedObject) {
      entry.trackedObject.getWorldPosition(out);
      return true;
    }
    const p = entry.panner;
    if (p.positionX) {
      out.set(p.positionX.value, p.positionY.value, p.positionZ.value);
      return true;
    }
    return false;
  }

  // ── id/channel convenience (dev mixer + back-compat; act on a match) ──────

  /** Stop every playing instance with this id, across channels. */
  stop(id: string, fadeOut = 0): void {
    for (const [h, entry] of [...this.playing]) {
      if (entry.id === id) this.stopEntry(h, fadeOut);
    }
  }

  stopChannel(channel: AudioChannel, fadeOut = 0): void {
    for (const [h, entry] of [...this.playing]) {
      if (entry.channel === channel) this.stopEntry(h, fadeOut);
    }
  }

  isChannelActive(channel: AudioChannel): boolean {
    for (const entry of this.playing.values()) {
      if (entry.channel === channel) return true;
    }
    return false;
  }

  isPlaying(id: string): boolean {
    for (const entry of this.playing.values()) {
      if (entry.id === id) return true;
    }
    return false;
  }

  /** Snapshot of every active instance. `handle` is the stable per-instance
   *  key (so N instances of one asset are distinct); `loop` distinguishes a
   *  sustained bed from a one-shot. Order is not stable. */
  listPlaying(): { handle: string; id: string; channel: AudioChannel; loop: boolean }[] {
    const out: { handle: string; id: string; channel: AudioChannel; loop: boolean }[] = [];
    for (const entry of this.playing.values()) {
      out.push({ handle: entry.handle, id: entry.id, channel: entry.channel, loop: entry.source.loop });
    }
    return out;
  }

  getEntryObject(id: string, channel: AudioChannel): Object3D | undefined {
    return this.findEntry(id, channel)?.trackedObject;
  }

  getEntryWorldPosition(id: string, channel: AudioChannel, out: Vector3): boolean {
    const entry = this.findEntry(id, channel);
    return entry ? this.entryWorldPosition(entry, out) : false;
  }

  setEntryVolume(id: string, channel: AudioChannel, v: number): void {
    const entry = this.findEntry(id, channel);
    if (entry) this.rampGain(entry, v);
  }

  getEntryVolume(id: string, channel: AudioChannel): number | undefined {
    return this.findEntry(id, channel)?.gain.gain.value;
  }

  isEntryPlaying(id: string, channel: AudioChannel): boolean {
    return this.findEntry(id, channel) !== undefined;
  }

  // ── handle-based control (precise per-instance; used by the engine) ───────

  stopHandle(handle: string, fadeOut = 0): void {
    this.stopEntry(handle, fadeOut);
  }

  isHandlePlaying(handle: string): boolean {
    return this.playing.has(handle);
  }

  setHandleVolume(handle: string, v: number): void {
    const entry = this.playing.get(handle);
    if (entry) this.rampGain(entry, v);
  }

  getHandleVolume(handle: string): number | undefined {
    return this.playing.get(handle)?.gain.gain.value;
  }

  /** A1 — current reach-cutoff gain for a spatial instance (1 within maxDistance,
   *  → REACH_FLOOR beyond). undefined for non-spatial instances. Lets the dev
   *  console + viz see how far a source actually carries. */
  getHandleReach(handle: string): number | undefined {
    return this.playing.get(handle)?.reachGain?.gain.value;
  }

  getHandleObject(handle: string): Object3D | undefined {
    return this.playing.get(handle)?.trackedObject;
  }

  getHandleWorldPosition(handle: string, out: Vector3): boolean {
    const entry = this.playing.get(handle);
    return entry ? this.entryWorldPosition(entry, out) : false;
  }

  /** Smooth a playing instance's gain to v over GAIN_RAMP (no zipper on slider/
   *  envelope drags). */
  private rampGain(entry: PlayingEntry, v: number): void {
    this.rampParam(entry.gain.gain, v);
  }

  /** A6 — ramp any AudioParam to target, re-anchoring at the live value so
   *  overlapping calls (a slider drag, a quick mute/unmute) stay smooth. */
  private rampParam(param: AudioParam, target: number, time = GAIN_RAMP): void {
    const now = this.ctx.currentTime;
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    param.linearRampToValueAtTime(target, now + time);
  }

  getChannelVolume(channel: AudioChannel): number {
    return this.channelBase[channel]; // the user value (not the possibly-ducked live gain)
  }

  /** N7 — the LIVE channel gain (base × any active duck). For the dev viz / the
   *  duck verification; getChannelVolume returns the user base instead. */
  getChannelLiveGain(channel: AudioChannel): number {
    return this.channels[channel].gain.value;
  }

  /** E2 — sweep the entry low-pass on the ambient bus. openness 1 = OPEN (clear,
   *  inside), 0 = MUFFLED (heard through the wall, outside). The view transition
   *  drives this so the interior tone rises/falls as you cross the doorway. The
   *  frequency lerps exponentially (perceptually even) over ENTRY_SWEEP. */
  setEntryOpenness(openness: number): void {
    const o = Math.max(0, Math.min(1, openness));
    const freq = ENTRY_LP_MUFFLED * Math.pow(ENTRY_LP_OPEN / ENTRY_LP_MUFFLED, o);
    const p = this.entryLowpass.frequency;
    const now = this.ctx.currentTime;
    p.cancelScheduledValues(now);
    p.setValueAtTime(Math.max(20, p.value), now); // exp ramp needs a positive start
    p.exponentialRampToValueAtTime(freq, now + ENTRY_SWEEP);
  }

  /** E2 verification / dev viz: the live entry-low-pass cutoff in Hz. */
  getEntryCutoff(): number {
    return this.entryLowpass.frequency.value;
  }

  setChannelVolume(channel: AudioChannel, v: number): void {
    this.channelBase[channel] = v;
    saveStored('channel_' + channel, v);
    this.rampParam(this.channels[channel].gain, v * this.channelDuck[channel], GAIN_RAMP); // honour any active duck
  }

  getMasterVolume(): number {
    return this._preferredMaster;
  }

  setMasterVolume(v: number): void {
    this._preferredMaster = v;
    saveStored('master', v);
    if (!this._muted) this.rampParam(this.master.gain, v); // A6 — smooth, no click on a level drag
  }
}
